// ===== CountrySystem.js - NEW SEPARATE FILE =====
// Save this as: src/CountrySystem.js
import React, { useState } from 'react';

import { AMAZON_MARKETPLACES } from './AmazonMarketplaces';


// ============================================
// COUNTRY SELECTOR FOR USERS
// ============================================
export const CountrySelector = ({ currentUser, onCountryChange }) => {
  return (
    <div className="bg-white rounded-xl shadow-md p-4 mb-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-gray-800 mb-1">
            🌍 Your Review marketplace
          </h3>
          <p className="text-xs text-gray-600">
            Select which Amazon marketplace you'll review on
          </p>
        </div>
        <select
          value={currentUser?.country || 'US'}
          onChange={(e) => onCountryChange(e.target.value)}
          className="px-4 py-2 border-2 border-indigo-200 rounded-lg font-medium text-sm"
        >
          {Object.entries(AMAZON_MARKETPLACES).map(([code, marketplace]) => (
            <option key={code} value={code}>
              {marketplace.flag} {marketplace.name}
            </option>
          ))}
        </select>
      </div>
      <div className="mt-2 text-xs text-gray-500">
        💡 You'll only see books available in your selected marketplace
      </div>
    </div>
  );
};

// ============================================
// COUNTRY BADGES FOR BOOK CARDS
// ============================================
export const CountryBadges = ({ targetCountries }) => {
  if (!targetCountries || targetCountries.length === 0) {
    return (
      <div className="mb-2">
        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
          🌍 Worldwide
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-1 mb-2">
      {targetCountries.map(code => {
        const marketplace = AMAZON_MARKETPLACES[code];
        if (!marketplace) return null;
        
        return (
          <span 
            key={code}
            className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full flex items-center gap-1"
            title={marketplace.name}
          >
            {marketplace.flag} {code}
          </span>
        );
      })}
    </div>
  );
};

// ============================================
// COUNTRY SELECTOR FOR BOOK UPLOAD
// ============================================
// Find your BookCountrySelector component in CountrySystem.js and replace it with this:

export const BookCountrySelector = ({ selectedCountries = [], onChange }) => {
  const [showAllMarketplaces, setShowAllMarketplaces] = useState(false);

  const handleToggle = (countryCode) => {
    if (selectedCountries.includes(countryCode)) {
      onChange(selectedCountries.filter(c => c !== countryCode));
    } else {
      onChange([...selectedCountries, countryCode]);
    }
  };

  const handleToggleAll = () => {
    if (showAllMarketplaces) {
      // Switch to "All marketplace"
      onChange([]);
    } else {
      // Switch to "Specific marketplace Only"
      // Don't clear selection, just show the UI
    }
    setShowAllMarketplaces(!showAllMarketplaces);
  };

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-gray-700">
        Target Amazon Marketplaces *
      </label>

      {/* Radio Options */}
      <div className="space-y-3">
        {/* All Marketplace Option */}
        <label 
          className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition ${
            !showAllMarketplaces 
              ? 'border-indigo-500 bg-indigo-50' 
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
          onClick={() => {
            setShowAllMarketplaces(false);
            onChange([]);
          }}
        >
          <input
            type="radio"
            checked={!showAllMarketplaces}
            onChange={() => {}}
            className="w-5 h-5 mt-0.5 text-indigo-600 focus:ring-indigo-500"
          />
          <div className="flex-1">
            <div className="font-semibold text-gray-800 flex items-center gap-2">
              🌍 All Amazon Marketplaces (Recommended)
            </div>
            <div className="text-sm text-gray-600 mt-1">
              Your book will be visible to reviewers from all Amazon marketplaces
            </div>
          </div>
        </label>

        {/* Specific Marketplace Option */}
        <label 
          className={`flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition ${
            showAllMarketplaces 
              ? 'border-purple-500 bg-purple-50' 
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
          onClick={() => setShowAllMarketplaces(true)}
        >
          <input
            type="radio"
            checked={showAllMarketplaces}
            onChange={() => {}}
            className="w-5 h-5 mt-0.5 text-purple-600 focus:ring-purple-500"
          />
          <div className="flex-1">
            <div className="font-semibold text-gray-800 flex items-center gap-2">
              🎯 Specific marketplace Only
            </div>
            <div className="text-sm text-gray-600 mt-1">
              Choose which Amazon marketplaces can see your book
            </div>
          </div>
        </label>
      </div>

      {/* Marketplace Selection Grid - Only show when "Specific marketplace Only" is selected */}
      {showAllMarketplaces && (
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
          <h4 className="font-medium text-gray-800 mb-3">Select marketplace:</h4>
          
          {/* Mobile: Single Column, Desktop: 4 Columns */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {Object.entries(AMAZON_MARKETPLACES).map(([code, info]) => (
              <label
  key={code}
  className={`flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer transition ${
    selectedCountries.includes(code)
      ? 'border-indigo-500 bg-indigo-50'
      : 'border-gray-200 bg-white hover:border-gray-300'
  }`}
>
  <input
    type="checkbox"
    checked={selectedCountries.includes(code)}
    onChange={() => handleToggle(code)}
    className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
  />
  <div className="flex-1 min-w-0">
    {/* ✅ REMOVED THE COUNTRY CODE - NOW ONLY SHOWS FLAG */}
    <div className="font-semibold text-gray-800 flex items-center gap-1.5">
      <span className="text-lg">{info.flag}</span>
      {/* DELETED: <span className="text-sm">{code}</span> */}
    </div>
    {/* ✅ AMAZON URL */}
    <div className="text-xs text-gray-600 truncate mt-0.5">
      {info.domain}
    </div>
  </div>
</label>
            ))}
          </div>

          {/* Selected Count */}
          <div className="mt-3 text-sm text-indigo-600 font-medium">
            ✓ Selected: {selectedCountries.length} {selectedCountries.length === 1 ? 'country' : 'countries'}
          </div>

          {/* Tip */}
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <span className="text-lg">💡</span>
              <p className="text-xs text-amber-800">
                <strong>Tip:</strong> "All marketplace" gets you more reviewers and faster reviews!
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// HELPER FUNCTIONS
// ============================================

// Check if book is available for user's country
export const isBookAvailableForCountry = (book, userCountry) => {
  // If no countries specified or empty array, book is available worldwide
  if (!book.targetCountries || book.targetCountries.length === 0) {
    return true;
  }
  
  // Check if user's country is in the target list
  return book.targetCountries.includes(userCountry || 'US');
};

// Filter books by user's country
export const filterBooksByCountry = (books, userCountry, currentUserId) => {
  return books.filter(book => {
    // Don't show user's own books
    if (book.uploadedBy === currentUserId) return false;
    
    // Check if book is available for user's country
    return isBookAvailableForCountry(book, userCountry);
  });
};

// Get marketplace info for display
export const getMarketplaceInfo = (countryCode) => {
  return AMAZON_MARKETPLACES[countryCode] || AMAZON_MARKETPLACES.US;
};

// Convert Amazon link to user's country domain
export const adaptAmazonLink = (originalLink, userCountry) => {
  if (!originalLink) return '#';
  
  const marketplace = AMAZON_MARKETPLACES[userCountry] || AMAZON_MARKETPLACES.US;
  
  // Replace amazon.com with user's country domain
  // This handles links like: https://amazon.com/dp/B123456
  const adaptedLink = originalLink.replace(/amazon\.(com|co\.uk|ca|de|fr|es|it|co\.jp|in|com\.au|com\.br|com\.mx|nl|sg|ae)/i, marketplace.domain);
  
  return adaptedLink;
};

// Get display text for country availability
export const getCountryAvailabilityText = (targetCountries) => {
  if (!targetCountries || targetCountries.length === 0) {
    return 'Available Worldwide';
  }
  
  if (targetCountries.length === 1) {
    const marketplace = AMAZON_MARKETPLACES[targetCountries[0]];
    return `${marketplace?.flag} ${marketplace?.name} Only`;
  }
  
  return `${targetCountries.length} Countries`;
};